/**
 * New mobile products page – same layout as Products (Mobile) but uses
 * SliderMobNew + ProductCardCarouselStack (stacked-slide carousel, no translate).
 * Use this page to verify if the gap is solved: /products-new
 */
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import './Products.scss';
import { SliderMobNew } from '../../Slider/SliderMobNew';
import { ProductGridSkeleton } from '../../Components/images/Mob/ProductGridSkeleton';
import { FixedBottom } from '../../Components/general/FixedBottom';
import { useParams, useNavigate } from 'react-router-dom';
import { useContextData } from '../../ApiContext';
import { MainContext } from '../../Contexts/MainContext';
import { isAndroid } from 'react-device-detect';
import {
    PHONE_CASE_CATEGORY,
    CASE_TYPES,
    DEVICES,
    toSelectedMap,
    keysFromSelectedMap,
    matchParamToKeys,
    buildPhoneCaseQueryParams,
    hasPhoneCaseFilters,
    DEFAULT_QUERY_PARAMS,
} from '../data/phoneCaseFilters';

function MobTopFilterIcon() {
    return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
            <path
                d="M4 6h16M7 12h10M10 18h4"
                stroke="currentColor"
                strokeWidth="1.65"
                strokeLinecap="round"
            />
        </svg>
    );
}

function MobSortChevron({ open }) {
    return (
        <svg
            className={`mob-sort-chevron-svg${open ? ' mob-sort-chevron-svg--open' : ''}`}
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden
        >
            <path
                d="M2.5 4.25L6 7.75l3.5-3.5"
                stroke="currentColor"
                strokeWidth="1.4"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}

const SORT_OPTIONS = [
    { value: 'popularity', label: 'Popularity' },
    { value: 'priceLowToHigh', label: 'Price: Low To High' },
    { value: 'priceHighToLow', label: 'Price: High To Low' },
];

export function ProductsMobNew({
    passed_url,
    hideOptions = false,
    deferProductGridForHomeHero = false,
    mobileCardVariant = 'stacked',
}) {
    const productScrollRootRef = useRef(null);
    const [sortDropdownOpen, setSortDropdownOpen] = useState(false);
    /** Hide bottom SORT|FILTER when footer enters view (replaces old filter-hider spacer). */
    const [hideStickyBarAtFooter, setHideStickyBarAtFooter] = useState(false);
    const navigate = useNavigate();
    const mainCtx = useContext(MainContext);
    const { category, subcategory } = useParams();
    const url = passed_url || '/products';
    const { queryParams, setQueryParams } = useContextData();

    const [sortOption, setSortOption] = useState('popularity');
    const [queryParamsInit, setQueryParamsInit] = useState(false);
    /** When true, SliderMobNew may fetch/render (after hero paint on Home). */
    const [allowProductSlider, setAllowProductSlider] = useState(!deferProductGridForHomeHero);
    const [selectedCaseTypes, setSelectedCaseTypes] = useState(() => toSelectedMap(CASE_TYPES));
    const [selectedDevices, setSelectedDevices] = useState(() => toSelectedMap(DEVICES));

    /** First page returned fewer than a full page — no infinite scroll; pair with filters for clear-only toolbar. */
    const [firstPageListMeta, setFirstPageListMeta] = useState({ resolved: false, exhausted: false });
    /**
     * Platform split:
     * - Android: use translate strip (LTR)
     * - iPhone/iOS (and others by default): keep stacked/current behavior
     * If caller explicitly passes a non-default variant, respect that override.
     */
    const effectiveMobileCardVariant =
        mobileCardVariant !== 'stacked'
            ? mobileCardVariant
            : (isAndroid ? 'translateStrip' : 'stacked');

    useEffect(() => {
        if (process.env.NODE_ENV !== 'development') return;
        console.log('[ProductsMobNew] card variant selection', {
            isAndroid,
            requestedMobileCardVariant: mobileCardVariant,
            effectiveMobileCardVariant,
        });
    }, [mobileCardVariant, effectiveMobileCardVariant]);
    const queryParamsKey = JSON.stringify(queryParams);
    useEffect(() => {
        setFirstPageListMeta({ resolved: false, exhausted: false });
    }, [queryParamsKey]);

    const handleFirstPageLoadMeta = useCallback(({ exhaustedOnFirstPage }) => {
        setFirstPageListMeta({ resolved: true, exhausted: !!exhaustedOnFirstPage });
    }, []);

    const hasActiveFilters = useMemo(
        () => hasPhoneCaseFilters(queryParams),
        [queryParams]
    );

    const showClearFiltersToolbar =
        firstPageListMeta.resolved && firstPageListMeta.exhausted && hasActiveFilters;

    useEffect(() => {
        if (showClearFiltersToolbar) setSortDropdownOpen(false);
    }, [showClearFiltersToolbar]);

    /** After sort / filter apply, bring the product grid (top of this block) into view — avoids staying scrolled to footer / empty tail. */
    const scrollProductsBlockIntoView = useCallback(() => {
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                productScrollRootRef.current?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start',
                });
            });
        });
    }, []);

    const clearAllFilters = useCallback(() => {
        setQueryParams((prev) => ({
            ...DEFAULT_QUERY_PARAMS,
            price: [0, 10000],
            ...(prev.sort != null && { sort: prev.sort }),
        }));
        scrollProductsBlockIntoView();
    }, [setQueryParams, scrollProductsBlockIntoView]);

    useEffect(() => {
        const matchedDevice = matchParamToKeys(subcategory, DEVICES);
        const matchedCaseType = matchParamToKeys(category, CASE_TYPES);
        const nextCaseTypes = toSelectedMap(CASE_TYPES, matchedCaseType);
        const nextDevices = toSelectedMap(DEVICES, matchedDevice);
        setSelectedCaseTypes(nextCaseTypes);
        setSelectedDevices(nextDevices);

        const queryList = buildPhoneCaseQueryParams({
            caseType: keysFromSelectedMap(nextCaseTypes),
            subcategory: keysFromSelectedMap(nextDevices),
            brand: [],
            color: [],
            price: queryParams.price || [0, 10000],
            sort: sortOption,
        });

        if (
            matchParamToKeys(category, [PHONE_CASE_CATEGORY]).length &&
            matchedDevice.length
        ) {
            queryList.category = [PHONE_CASE_CATEGORY];
            queryList.subcategory = matchedDevice;
        }

        setQueryParams(queryList);
        setQueryParamsInit(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        setSortOption(queryParams.sort || 'popularity');
        if (Array.isArray(queryParams.caseType)) {
            setSelectedCaseTypes(toSelectedMap(CASE_TYPES, queryParams.caseType));
        }
        if (Array.isArray(queryParams.subcategory)) {
            setSelectedDevices(toSelectedMap(DEVICES, queryParams.subcategory));
        }
    }, [queryParams]);

    const currentSortLabel = SORT_OPTIONS.find((o) => o.value === sortOption)?.label || sortOption;

    useEffect(() => {
        const onFilterApplied = () => scrollProductsBlockIntoView();
        window.addEventListener('mob-products-filter-applied', onFilterApplied);
        return () => window.removeEventListener('mob-products-filter-applied', onFilterApplied);
    }, [scrollProductsBlockIntoView]);

    useEffect(() => {
        if (hideOptions) {
            setHideStickyBarAtFooter(false);
            return undefined;
        }
        const footer = document.querySelector('.la-mob-footer');
        if (!footer || typeof IntersectionObserver === 'undefined') return undefined;

        const observer = new IntersectionObserver(
            ([entry]) => {
                setHideStickyBarAtFooter(!!entry?.isIntersecting);
            },
            { root: null, threshold: 0, rootMargin: '0px 0px 0px 0px' }
        );
        observer.observe(footer);
        return () => observer.disconnect();
    }, [hideOptions]);

    /**
     * Home only: start the product grid (fetch + loader) only after the document has
     * finished loading, then wait 5s so hero / above-the-fold can settle on iOS.
     */
    useEffect(() => {
        if (!deferProductGridForHomeHero) {
            setAllowProductSlider(true);
            return;
        }
        let cancelled = false;
        let postLoadTimeoutId = 0;

        const scheduleFiveSecondsAfterLoad = () => {
            if (cancelled) return;
            postLoadTimeoutId = window.setTimeout(() => {
                if (!cancelled) setAllowProductSlider(true);
            }, 5000);
        };

        if (typeof document !== 'undefined' && document.readyState === 'complete') {
            scheduleFiveSecondsAfterLoad();
        } else if (typeof window !== 'undefined') {
            window.addEventListener('load', scheduleFiveSecondsAfterLoad, { once: true });
        } else {
            scheduleFiveSecondsAfterLoad();
        }

        return () => {
            cancelled = true;
            if (typeof window !== 'undefined') {
                window.removeEventListener('load', scheduleFiveSecondsAfterLoad);
            }
            if (postLoadTimeoutId) window.clearTimeout(postLoadTimeoutId);
        };
    }, [deferProductGridForHomeHero]);

    const openFilterOverlay = useCallback(() => {
        if (mainCtx?.setFilterOverlayOpen) {
            mainCtx.setFilterOverlayOpen(true);
        } else {
            navigate('/filter', {
                state: { from: window.location.pathname, scrollY: window.scrollY },
            });
        }
    }, [mainCtx, navigate]);

    const handleStickyBarSortChange = (value) => {
        setSortOption(value);
        setQueryParams((prev) => {
            const next = { ...prev };
            if (value === 'popularity') delete next.sort;
            else next.sort = value;
            return next;
        });
        scrollProductsBlockIntoView();
    };

    return (
        <div ref={productScrollRootRef} className="mob-product product-mob-scroll-root">
            <div
                className={`innerFilter2 mob-sort-only${showClearFiltersToolbar ? ' mob-sort-only--clear-toolbar' : ''}`}
                style={hideOptions ? { display: 'none' } : null}
            >
                {showClearFiltersToolbar ? (
                    <div className="mob-products-clear-row">
                        <p className="mob-products-clear-row__text">
                            <span className="mob-products-clear-row__line">All matching products are listed.</span>
                            <span className="mob-products-clear-row__line">Clear filters to see more items.</span>
                        </p>
                        <button type="button" className="mob-products-clear-row__btn" onClick={clearAllFilters}>
                            Clear filters
                        </button>
                    </div>
                ) : (
                    <div
                        className={`mob-products-controls${sortDropdownOpen ? ' mob-products-controls--sort-open' : ''}${hasActiveFilters ? ' mob-products-controls--filters-active' : ''}`}
                    >
                        <button
                            type="button"
                            className={`mob-top-filter-btn${hasActiveFilters ? ' mob-top-filter-btn--active' : ''}`}
                            onClick={openFilterOverlay}
                            aria-label={hasActiveFilters ? 'Open filters (filters applied)' : 'Open filters'}
                        >
                            <span className="mob-top-filter-btn__inner">
                                <MobTopFilterIcon />
                                <span className="mob-top-filter-btn__label">
                                    Filter
                                    {hasActiveFilters ? (
                                        <span className="mob-top-filter-btn__applied" aria-hidden="true">
                                            ✓
                                        </span>
                                    ) : null}
                                </span>
                            </span>
                        </button>
                        <span className="mob-products-controls__divider" aria-hidden />
                        <div className="mob-sort-dropdown">
                            <button
                                type="button"
                                className="mob-sort-trigger"
                                onClick={() => setSortDropdownOpen(!sortDropdownOpen)}
                                aria-expanded={sortDropdownOpen}
                                aria-haspopup="listbox"
                            >
                                <span className="mob-sort-trigger__copy">
                                    <span className="mob-sort-trigger__prefix">Sort by</span>
                                    <span className="mob-sort-label">{currentSortLabel}</span>
                                </span>
                                <MobSortChevron open={sortDropdownOpen} />
                            </button>
                            {sortDropdownOpen && (
                                <ul className="mob-sort-list" role="listbox" aria-label="Sort options">
                                    {SORT_OPTIONS.map((opt) => (
                                        <li
                                            key={opt.value}
                                            role="option"
                                            aria-selected={sortOption === opt.value}
                                            className={sortOption === opt.value ? 'selected' : ''}
                                            onClick={() => {
                                                setSortOption(opt.value);
                                                setSortDropdownOpen(false);
                                                setQueryParams((prev) => {
                                                    const next = { ...prev };
                                                    if (opt.value === 'popularity') delete next.sort;
                                                    else next.sort = opt.value;
                                                    return next;
                                                });
                                                scrollProductsBlockIntoView();
                                            }}
                                        >
                                            <span className="mob-sort-option-text">{opt.label}</span>
                                            {sortOption === opt.value && <span className="mob-sort-check" aria-hidden="true">✓</span>}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>
                )}
            </div>

            <div className="mainContent">
                <div className="ContentWrapper">
                    {!allowProductSlider ? (
                        <ProductGridSkeleton cells={10} />
                    ) : (
                        <SliderMobNew
                            url={url}
                            queryParams={queryParams}
                            queryParamsInit={queryParamsInit}
                            onFirstPageLoadMeta={handleFirstPageLoadMeta}
                            mobileCardVariant={effectiveMobileCardVariant}
                        />
                    )}
                </div>
            </div>

            {!hideOptions && (
                <div
                    className={`products-sticky-bar-wrapper${hideStickyBarAtFooter ? ' products-sticky-bar-wrapper--hidden' : ''}`}
                    aria-hidden={hideStickyBarAtFooter}
                >
                    <FixedBottom
                        isSticky={true}
                        sortOptions={SORT_OPTIONS}
                        sortOption={sortOption}
                        onSortChange={handleStickyBarSortChange}
                        hasActiveFilters={hasActiveFilters}
                    />
                </div>
            )}
        </div>
    );
}
